export type ExecJsEffect = {
  actionKey: string;
  actionTitle: string;
  actionIcon: string;
  effectIndex: number;
  code: string;
};

export type GroupedEffect = {
  actionKey: string;
  actionTitle: string;
  actionIcon: string;
  effects: { index: number; code: string }[];
};

type EffectObject = {
  $statement?: string;
  arguments?: { code?: string }[];
  function?: {
    $expression?: string;
    path?: string;
  };
};

/**
 * Check if an effect object matches the execJs pattern:
 * - $statement === "FUNCTION_CALL"
 * - function.$expression === "EXTERNAL_FUNCTION"
 * - function.path === "execJs"
 */
const isExecJsEffect = (effect: unknown): effect is EffectObject => {
  if (typeof effect !== 'object' || effect === null) return false;

  const obj = effect as EffectObject;

  return (
    obj.$statement === 'FUNCTION_CALL' &&
    obj.function?.$expression === 'EXTERNAL_FUNCTION' &&
    obj.function?.path === 'execJs'
  );
};

/**
 * Extract the JavaScript code from an execJs effect.
 */
const extractCodeFromEffect = (effect: EffectObject): string | null => {
  const code = effect.arguments?.at(0)?.code;
  return typeof code === 'string' ? code : null;
};

/**
 * Extract all execJs effects from workflow config actions.
 *
 * Looks for effects matching the pattern:
 * - $statement === "FUNCTION_CALL"
 * - function.$expression === "EXTERNAL_FUNCTION"
 * - function.path === "execJs"
 * - arguments[0].code contains the JavaScript code
 */
export const extractExecJsEffects = (
  actions: Record<string, unknown> | null | undefined
): ExecJsEffect[] => {
  if (!actions || typeof actions !== 'object') return [];

  const results: ExecJsEffect[] = [];

  for (const [actionKey, actionValue] of Object.entries(actions)) {
    if (typeof actionValue !== 'object' || actionValue === null) continue;

    const action = actionValue as {
      effects?: unknown[];
      title?: string;
      icon?: string;
    };
    const effects = action.effects;

    if (!Array.isArray(effects)) continue;

    // Extract title and icon with fallbacks
    const actionTitle = action.title ?? actionKey;
    const actionIcon = action.icon ?? 'Document';

    for (const [effectIndex, effect] of effects.entries()) {
      if (isExecJsEffect(effect)) {
        const code = extractCodeFromEffect(effect);
        if (code) {
          results.push({
            actionKey,
            actionTitle,
            actionIcon,
            effectIndex,
            code
          });
        }
      }
    }
  }

  return results;
};

/**
 * Group effects by action key for display in accordion.
 */
export const groupEffectsByAction = (
  effects: ExecJsEffect[]
): GroupedEffect[] => {
  const grouped = new Map<
    string,
    {
      title: string;
      icon: string;
      effects: { index: number; code: string }[];
    }
  >();

  for (const effect of effects) {
    const existing = grouped.get(effect.actionKey);
    const effectData = { index: effect.effectIndex, code: effect.code };

    if (existing) {
      existing.effects.push(effectData);
    } else {
      grouped.set(effect.actionKey, {
        title: effect.actionTitle,
        icon: effect.actionIcon,
        effects: [effectData]
      });
    }
  }

  return [...grouped.entries()].map(([actionKey, data]) => ({
    actionKey,
    actionTitle: data.title,
    actionIcon: data.icon,
    effects: data.effects
  }));
};

/**
 * Type for tracking code changes: Map<actionKey, Map<effectIndex, newCode>>
 */
export type CodeChanges = Map<string, Map<number, string>>;

/**
 * Apply code changes to the workflow config actions object.
 * Returns a new actions object with the updated code values.
 */
export const applyCodeChangesToActions = (
  actions: Record<string, unknown>,
  changes: CodeChanges
): Record<string, unknown> => {
  // Deep clone the actions object
  const newActions = JSON.parse(JSON.stringify(actions)) as Record<
    string,
    unknown
  >;

  for (const [actionKey, effectChanges] of changes) {
    const action = newActions[actionKey];
    if (typeof action !== 'object' || action === null) continue;

    const actionObj = action as { effects?: unknown[] };
    if (!Array.isArray(actionObj.effects)) continue;

    for (const [effectIndex, newCode] of effectChanges) {
      const effect = actionObj.effects[effectIndex];
      if (isExecJsEffect(effect)) {
        // Update the code in arguments[0].code
        const effectObj = effect as EffectObject;
        if (effectObj.arguments?.[0]) {
          effectObj.arguments[0].code = newCode;
        }
      }
    }
  }

  return newActions;
};
